import { Diamond, LogoSaml } from "components/icons/generated";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import setLocation from "shared/setLocation";
import { BillingTierFeature, SamlSettings } from "types/generated";
import useTierFeature from "views/Account/hooks/useTierFeature";
import ActionCard from "ds/components/ActionCard";
import TierInfo from "components/TierInfo";

import { openSingleSignOnDrawer } from "../components/Drawer";
import SettingsSingleSignOnCreateSAML from "./Create";
import SettingsSingleSignOnDetailsSAML from "./Details";
import SettingsSingleSignOnEditSAML from "./Edit";
import { showDisableConfirmation } from "./Disable";

type SettingsSingleSignOnSAMLProps = {
  integration: SamlSettings | undefined;
  oidcAlreadyEnabled: boolean;
  showUpgradeBanner: boolean;
  showUnsupportedBanner: boolean;
};

const SettingsSingleSignOnSAML = ({
  integration,
  oidcAlreadyEnabled,
  showUpgradeBanner,
  showUnsupportedBanner,
}: SettingsSingleSignOnSAMLProps) => {
  const isTierContainSAML = useTierFeature(BillingTierFeature.CustomSsoSaml);
  const isIntegrationActive = !!integration?.adminLogin;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSAML,
    defaultCallbackTrackProperties: {
      method: "SAML",
    },
  });

  const onPress = () => {
    openSingleSignOnDrawer({
      children: ({ setIsDirty }) => <SettingsSingleSignOnCreateSAML setIsDirty={setIsDirty} />,
      isDismissable: false,
    });
  };

  const handleActivate = () => {
    setLocation("/saml/start");
    trackSegmentAnalyticsEvent("Activate Click");
  };

  const handleDisable = () => {
    showDisableConfirmation();
    trackSegmentAnalyticsEvent("Disable Click");
  };

  const handleShowDetails = () => {
    if (integration) {
      openSingleSignOnDrawer({
        children: () => <SettingsSingleSignOnDetailsSAML integration={integration} />,
      });
    }
  };

  const handleEditClaimMappings = () => {
    if (integration) {
      openSingleSignOnDrawer({
        children: ({ setIsDirty }) => (
          <SettingsSingleSignOnEditSAML integration={integration} setIsDirty={setIsDirty} />
        ),
        isDismissable: true,
      });
    }
    trackSegmentAnalyticsEvent("Edit Click");
  };

  return (
    <ActionCard
      title="SAML"
      icon={LogoSaml}
      actionComponent={
        <Box gap="medium">
          {/* Regular "Set up" flow */}
          {isTierContainSAML && !integration && !oidcAlreadyEnabled && (
            <Button variant="secondary" onPress={onPress}>
              Set up
            </Button>
          )}

          {/* Disabled "Set up" flow because the SAML is no available in tier */}
          {!isTierContainSAML && !integration && (
            <Tooltip
              active
              on={(props) => (
                <Button variant="secondary" disabled {...props}>
                  Set up
                </Button>
              )}
            >
              Upgrade account to use SAML SSO 2.0
            </Tooltip>
          )}

          {/* Disabled "Set up" button because OIDC is already enabled */}
          {isTierContainSAML && !integration && oidcAlreadyEnabled && (
            <Tooltip
              active
              on={(props) => (
                <Button variant="secondary" disabled {...props}>
                  Set up
                </Button>
              )}
            >
              OIDC integration already present, can't set up SAML
            </Tooltip>
          )}

          {isTierContainSAML && integration && !isIntegrationActive && (
            <Button variant="primary" onPress={handleActivate}>
              Activate
            </Button>
          )}

          {integration && (
            <DropdownMenuEllipsis tooltip="SAML actions" dotsSize="medium">
              <DropdownMenuItem onAction={handleShowDetails}>See details</DropdownMenuItem>
              <DropdownMenuItem
                tooltip={
                  !isTierContainSAML
                    ? "Upgrade account to be able to edit your configuration"
                    : undefined
                }
                isDisabled={!isTierContainSAML}
                onAction={handleEditClaimMappings}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onAction={handleDisable} danger>
                Unlink
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          )}
        </Box>
      }
      titleSuffix={
        <Box direction="row" gap="medium" align="center">
          {!isTierContainSAML && (
            <Badge variant="purple" type="strong" startIcon={Diamond} iconOnly />
          )}
          {isTierContainSAML && integration && isIntegrationActive && (
            <Badge variant="green" type="strong" text="Active" />
          )}
        </Box>
      }
      description={
        <Box direction="column" gap="medium">
          Connect your Spacelift account to a SAML 2.0 supporting identity provider.
          {showUpgradeBanner && (
            <TierInfo
              variant="promo"
              type="banner"
              feature={BillingTierFeature.CustomSsoSaml}
              title="Upgrade account to use SAML SSO 2.0"
            >
              Add extra layer of security to your account by enabling SAML SSO 2.0
            </TierInfo>
          )}
          {showUnsupportedBanner && (
            <TierInfo
              type="banner"
              variant="danger"
              feature={BillingTierFeature.CustomSsoSaml}
              title="SAML SSO 2.0 is not supported by your plan"
            >
              You can still use or disable the integration, but to make changes, please upgrade to
              the <strong>Enterprise</strong> plan.
            </TierInfo>
          )}
        </Box>
      }
    />
  );
};

export default SettingsSingleSignOnSAML;
